// Import dependencies
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { getIntegration } from '@automator/features/page-template-library/utilities';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

/**
 * A mixin for creating a template item, including an integration icon, integration name, and a sentence.
 *
 * @param {typeof LitElement} superClass - The superclass to extend.
 * @return {typeof LitElement} The extended class with additional functionality.
 */
const TemplateItemMixin = ( superClass ) => class extends superClass {
	/**
	 * The code of the associated integration.
	 *
	 * @type {String}
	 * @property {String} integrationCode - Represents the code of the integration.
	 * Attribute name in HTML: `integration-code`.
	 */
	@property( { type: String, attribute: 'integration-code' } )
	accessor integrationCode;

	/**
	 * The title of the template item.
	 *
	 * @type {String}
	 * @property {String} title - The title displayed in the sentence section.
	 * Attribute name in HTML: `title`.
	 * Default: `''`.
	 */
	@property( { type: String } )
	accessor title = '';

	/**
	 * The HTML version of the title.
	 *
	 * @type {String}
	 * @property {String} titleHTML - A string containing HTML content for the title.
	 * Attribute name in HTML: none (non-reflected property).
	 */
	@property( { type: String, attribute: false } )
	accessor titleHTML = '';

	/**
	 * Indicates whether the item requires the Automator Pro
	 *
	 * @type {Boolean}
	 * @property {Boolean} isPro - Specifies if the recipe requires a pro license.
	 * Attribute name in HTML: `is-pro`.
	 * Default: `false`.
	 */
	@property( { type: Boolean, attribute: 'is-pro' } )
	accessor isPro = false;

	/**
	 * Indicates whether the item requires the Automator Elite
	 *
	 * @type {Boolean}
	 * @property {Boolean} isElite - Specifies if the recipe requires an elite license.
	 * Attribute name in HTML: `is-elite`.
	 * Default: `false`.
	 */
	@property( { type: Boolean, attribute: 'is-elite' } )
	accessor isElite = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="item">
				${ this._templateIntegrationIcon }
				${ this._templateTags }
				${ this._templateSentence }
			</div>
		`;
	}

	/**
	 * Renders the integration icon
	 *
	 * @return {TemplateResult} The integration icon
	 */
	get _templateIntegrationIcon() {
		return html`
			<div class="item__icon">
				<uap-template-integration-icon 
					size="16" 
					integration-code="${ this.integrationCode }" 
					show-tooltip
				></uap-template-integration-icon>
			</div>
		`;
	}

	/**
	 * Renders the tags
	 *
	 * @return {TemplateResult} The tags
	 */
	get _templateTags() {
		return html`
			<div class="item__tags">
				${ this._templateIntegrationName }
				${ this._templatePremiumBadge }
			</div>
		`;
	}

	/**
	 * Renders the integration name
	 *
	 * @return {TemplateResult} The integration name
	 */
	get _templateIntegrationName() {
		return html`
			<div class="item__integration-name">
				<uo-chip size="small">${ this.integrationName }</uo-chip>
			</div>
		`;
	}

	/**
	 * Gets the pro badge for the template.
	 *
	 * @return {TemplateResult|undefined} The pro badge element.
	 */
	get _templatePremiumBadge() {
		// Check if it requires Pro
		if ( this.isPro ) {
			return html`
				<div class="item__premium-tag">
					<uo-chip 
						size="small"
						color="success"
					>
						${ escapeHTML( __( 'Pro', 'uncanny-automator' ) ) }
					</uo-chip>
				</div>
			`;
		}

		// Check if it requires Elite
		if ( this.isElite ) {
			return html`
				<div class="item__premium-tag">
					<uo-chip 
						size="small"
						color="success"
					>
						${ escapeHTML( __( 'Pro', 'uncanny-automator' ) ) }
					</uo-chip>
				</div>
			`;
		}
	}

	/**
	 * Renders the item sentence
	 *
	 * @return {TemplateResult} The sentence
	 */
	get _templateSentence() {
		return html`
			<div class="item__sentence">
				<uap-template-sentence .sentence="${ this.titleHTML }"></uap-template-sentence>
			</div>
		`;
	}

	/**
	 * Tries to get the integration name
	 *
	 * @return {String|undefined} The integration name
	 */
	get integrationName() {
		return getIntegration( 'code', this.integrationCode )?.name;
	}
}

export const AutomatorTemplateItem = TemplateItemMixin( LitElement );
